import React, { useState } from 'react'

import {
    Badge,
    Checkbox,
    Chip,
    IconButton,
    Tooltip,
    useTheme,
} from '@material-ui/core'
import MessageIcon from '@material-ui/icons/Message'
import AddBoxIcon from '@material-ui/icons/AddBox'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import GroupIcon from '@material-ui/icons/Group'
import AssignmentTurnedInIcon from '@material-ui/icons/AssignmentTurnedIn'

import {
    Attachment,
    Container,
    EditSpan,
    Selector,
    StatusBadge,
} from '../../components'
import {
    AreaConfig,
    Damage,
    DamageConfig,
    DamageStatus,
    DamageTransaction,
    getDamageStatusColor,
    getDamageStatusName,
    InventoryConfig,
    Lease,
    ModelMap,
    Unit,
    WorkSpaceUser,
} from '../../models'
import { axiosInstance, currencyFormatter, toMMDDYYYY } from '../../helpers'

import { DamageTransactionRow } from './DamageTransactionRow'
import { DamageStatusChangeButtons } from './DamageStatusChangeButtons'

import { toast } from 'react-toastify'
import { MoveInModal } from './MoveInModal'
import { CurrentTenantsModal } from './CurrentTenantsModal'
import { UpdateDamageRequest, useLease } from '../../hooks'
import { Build } from '@material-ui/icons'
import { typographyStyle } from '../../theme/typographyStyle'

interface Props {
    damage: Damage
    selected: boolean
    messageDrawerOpen?: boolean
    workspaceUser?: WorkSpaceUser
    damageConfigList?: DamageConfig[]
    displayProblems: boolean
    onSelect: () => void
    handleStatusChange: (
        damages: Damage[],
        status: DamageStatus,
    ) => Promise<void> | void | undefined
    removeDamageTransaction: (
        transaction: DamageTransaction,
        damage: Damage,
    ) => void
    onOpenMessageDrawer: (channelId: number, damageId: number) => void
    updateDamage: (
        damage: Damage,
        request: UpdateDamageRequest,
    ) => Promise<Damage | void>
    openPopover: (
        event: React.MouseEvent<HTMLElement>,
        damageId: number,
    ) => void
    areaConfigMap?: ModelMap<AreaConfig>
    invConfigMap?: ModelMap<InventoryConfig>
    unitMap?: ModelMap<Unit>
    onClickDamageButton?: () => void
}

export const DamageRow = (props: Props) => {
    const {
        damage,
        handleStatusChange,
        messageDrawerOpen,
        workspaceUser,
        damageConfigList,
        updateDamage,
        openPopover,
        displayProblems,
        areaConfigMap,
        invConfigMap,
        unitMap,
        selected,
        onSelect,
        onClickDamageButton,
    } = props

    const theme = useTheme()

    const damageCost = damage.amount * damage.cost

    const description = damage.damage_config
        ? damage.damage_config.description
        : damage.description

    const damageDisabled =
        damage.status !== DamageStatus.PENDING &&
        damage.status !== DamageStatus.PRE_APPROVED

    const [lease, setLease] = useState<Lease>()
    const [currentTenantLeases, setCurrentTenantLeases] = useState<Lease[]>([])
    const [isLoadingLeases, setIsLoadingLeases] = useState(false)
    const [showCurrentTenantsModal, setShowCurrentTenantsModal] = useState(
        false,
    )
    const [issueLeaseOpen, setIssueLeaseOpen] = useState(false)

    const issueFilteredLease: Lease | undefined = (() => {
        if (damage.corresponding_issue == null) return undefined
        for (const dt of damage.damage_transactions) {
            const lease = dt.lease
            const matchingInv = lease.tenant_move_in_inspection?.inventory_inspection?.find(
                (inv) =>
                    inv.issue?.some(
                        (iss) => iss.id === damage.corresponding_issue,
                    ),
            )
            if (matchingInv) {
                return {
                    ...lease,
                    tenant_move_in_inspection: lease.tenant_move_in_inspection
                        ? {
                              ...lease.tenant_move_in_inspection,
                              inventory_inspection: [
                                  {
                                      ...matchingInv,
                                      issue: matchingInv.issue?.filter(
                                          (iss) =>
                                              iss.id ===
                                              damage.corresponding_issue,
                                      ),
                                  },
                              ],
                          }
                        : null,
                }
            }
        }
        return undefined
    })()

    const handleOpenIssueDialog = (e: React.MouseEvent) => {
        e.stopPropagation()
        setIssueLeaseOpen(true)
    }

    const fetchCurrentTenantLeases = async () => {
        if (!damage.unit) return

        setIsLoadingLeases(true)
        try {
            const response = await axiosInstance.get(
                `lease/get_current_tenant_leases/`,
                {
                    params: {
                        unit_id: damage.unit,
                    },
                },
            )
            setCurrentTenantLeases(response.data)
        } catch (error) {
            console.error('Error fetching current tenant leases:', error)
            toast.error('Failed to fetch current tenant leases')
        } finally {
            setIsLoadingLeases(false)
        }
    }

    return (
        <Container
            style={{
                margin: theme.spacing(1),
                backgroundColor: messageDrawerOpen
                    ? theme.palette.primary.light
                    : selected
                    ? theme.palette.action.selected
                    : theme.palette.grey[100],
                borderRadius: theme.shape.borderRadius,
                border: selected
                    ? `2px solid ${theme.palette.primary.main}`
                    : `1px solid ${theme.palette.grey[600]}`,
                minHeight: 240,
                overflowX: 'auto',
                position: 'relative',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
            }}
            onClick={onSelect}
        >
            {/* Selection indicator */}
            {selected && (
                <div
                    style={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        zIndex: 10,
                    }}
                >
                    <CheckCircleIcon color="primary" fontSize="small" />
                </div>
            )}

            {/* Left Column: Damage Details */}
            <Container
                style={{
                    padding: theme.spacing(1),
                    minWidth: LEFT_COL_W,
                    maxWidth: LEFT_COL_W,
                    overflow: 'hidden', // Prevent column from expanding and causing horizontal scrolling
                }}
            >
                <Container
                    style={{
                        flexDirection: 'column',
                        flex: 1,
                        height: '100%',
                        justifyContent: 'space-between',
                    }}
                >
                    {/* Top content section */}
                    <div>
                        {/* Top section: Inspection and Date */}
                        <Container
                            style={{ alignItems: 'center', marginBottom: 6 }}
                        >
                            <Tooltip title={damage.inspection_name}>
                                <span
                                    style={{
                                        ...typographyStyle(
                                            theme.typography.caption,
                                        ),
                                        color: theme.palette.text.secondary,
                                        backgroundColor:
                                            theme.palette.grey[200],
                                        padding: '2px 6px',
                                        borderRadius: 4,
                                        display: 'inline-block',
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        maxWidth: LEFT_COL_W - 80, // Leave room for date
                                        cursor: 'default',
                                    }}
                                >
                                    {damage.inspection_name}
                                </span>
                            </Tooltip>
                            <div style={{ flex: 1 }} />
                            <span
                                style={{
                                    ...typographyStyle(
                                        theme.typography.caption,
                                    ),
                                    color: theme.palette.text.secondary,
                                    flexShrink: 0,
                                }}
                            >
                                {toMMDDYYYY(new Date(damage.created_date))}
                            </span>
                        </Container>

                        {/* Location hierarchy with folder path */}
                        <Container
                            style={{ marginBottom: 4, alignItems: 'center' }}
                        >
                            <span
                                style={{
                                    ...typographyStyle(
                                        theme.typography.caption,
                                    ),
                                    color: theme.palette.text.secondary,
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    width: LEFT_COL_W - 2 * theme.spacing(1),
                                }}
                            >
                                {damage.folder_path}
                                {damage.folder_name}
                            </span>
                        </Container>

                        {/* Unit and area info */}
                        <Container
                            style={{ alignItems: 'center', marginBottom: 6 }}
                        >
                            <span
                                style={{
                                    ...typographyStyle(theme.typography.h6),
                                    fontWeight: 700,
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    fontSize: '1.05rem',
                                    lineHeight: 1.2,
                                    maxWidth: '40%',
                                }}
                            >
                                {damage.unit_name}
                            </span>
                            <span
                                style={{
                                    ...typographyStyle(theme.typography.body2),
                                    marginLeft: 8,
                                    color: theme.palette.text.primary,
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    flex: 1,
                                }}
                            >
                                • {damage.area_config_name} ({damage.area_label}
                                )
                            </span>
                        </Container>

                        {/* Damage Description */}
                        <div onClick={(e) => e.stopPropagation()}>
                            <EditSpan
                                style={{
                                    ...typographyStyle(
                                        theme.typography.subtitle1,
                                    ),
                                    overflow: 'hidden',
                                    maxLines: 2,
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'normal',
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    maxWidth: LEFT_COL_W - 2 * theme.spacing(1),
                                    marginBottom: 6,
                                    fontSize: '0.9rem',
                                    fontWeight:
                                        theme.typography.fontWeightMedium,
                                    lineHeight: 1.3,
                                }}
                                disabled={damageDisabled}
                                hideSubmitButtons
                                customPopoverContent={
                                    <Container
                                        style={{ flexDirection: 'column' }}
                                    >
                                        <Selector
                                            label="Damage Config"
                                            searchable
                                            currentValue={
                                                damage.damage_config?.id ?? -1
                                            }
                                            data={damageConfigList ?? []}
                                            customStyle={{
                                                formControl: {
                                                    margin: 0,
                                                    width: 200,
                                                },
                                            }}
                                            onChange={(
                                                event: React.ChangeEvent<{
                                                    value: unknown
                                                }>,
                                            ) => {
                                                const request: UpdateDamageRequest = {
                                                    damage_config: event.target
                                                        .value as number,
                                                }

                                                updateDamage(
                                                    damage,
                                                    request,
                                                ).catch((e) => {
                                                    toast.error(
                                                        `Error updating damage ${e}`,
                                                    )
                                                })
                                            }}
                                            getDisplayString={(
                                                damageConfig: DamageConfig,
                                            ) => `${damageConfig.description}`}
                                        />
                                    </Container>
                                }
                            >
                                {description}
                            </EditSpan>
                        </div>

                        {/* Damage Cost */}
                        <div onClick={(e) => e.stopPropagation()}>
                            <Container
                                style={{
                                    overflow: 'hidden',
                                    maxLines: 1,
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    width: LEFT_COL_W - 2 * theme.spacing(1),
                                    alignItems: 'center',
                                    minHeight: 28,
                                    backgroundColor: theme.palette.grey[50],
                                    padding: '3px 8px',
                                    borderRadius: 4,
                                    marginBottom: 6,
                                }}
                            >
                                <EditSpan
                                    style={{
                                        ...typographyStyle(
                                            theme.typography.subtitle2,
                                        ),
                                    }}
                                    dataType="number"
                                    disabled={damageDisabled}
                                    onSave={(newValue) => {
                                        const request: UpdateDamageRequest = {
                                            cost: newValue,
                                        }

                                        updateDamage(damage, request).catch(
                                            (e) => {
                                                toast.error(
                                                    `Error updating damage ${e}`,
                                                )
                                            },
                                        )
                                    }}
                                >
                                    {currencyFormatter.format(damage.cost)}
                                </EditSpan>
                                <span
                                    style={{
                                        ...typographyStyle(
                                            theme.typography.subtitle2,
                                        ),
                                        margin: '0 4px',
                                    }}
                                >
                                    ×
                                </span>
                                <EditSpan
                                    style={{
                                        ...typographyStyle(
                                            theme.typography.subtitle2,
                                        ),
                                    }}
                                    dataType="number"
                                    disabled={damageDisabled}
                                    onSave={(newValue) => {
                                        const request: UpdateDamageRequest = {
                                            amount: Number(newValue),
                                        }

                                        updateDamage(damage, request).catch(
                                            (e) => {
                                                toast.error(
                                                    `Error updating damage ${e}`,
                                                )
                                            },
                                        )
                                    }}
                                >
                                    {damage.amount}
                                </EditSpan>
                                <span
                                    style={{
                                        ...typographyStyle(
                                            theme.typography.subtitle2,
                                        ),
                                        margin: '0 4px',
                                    }}
                                >
                                    =
                                </span>
                                <span
                                    style={{
                                        ...typographyStyle(
                                            theme.typography.subtitle1,
                                        ),
                                        fontWeight:
                                            theme.typography.fontWeightBold,
                                    }}
                                >
                                    {currencyFormatter.format(damageCost)}
                                </span>
                            </Container>
                        </div>
                    </div>

                    {/* Footer section: Status + Actions */}
                    <div onClick={(e) => e.stopPropagation()}>
                        {damage.corresponding_issue != null && (
                            <div style={{ marginBottom: 4 }}>
                                <Chip
                                    label="Potential Issue"
                                    size="small"
                                    onClick={handleOpenIssueDialog}
                                    style={{
                                        backgroundColor:
                                            theme.palette.warning.main,
                                        color: theme.palette.common.white,
                                        fontWeight: 'bold',
                                        fontSize: '0.75rem',
                                        cursor: 'pointer',
                                    }}
                                />
                            </div>
                        )}
                        <Container
                            style={{
                                flexDirection: 'row',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                marginTop: 'auto', // Push to bottom
                            }}
                        >
                            <StatusBadge
                                text={getDamageStatusName(damage.status)}
                                customStyle={{
                                    container: {
                                        width: 120,
                                        height: 24,
                                        backgroundColor: getDamageStatusColor(
                                            damage,
                                        ),
                                        fontSize: '0.85rem',
                                    },
                                    text: {
                                        fontWeight: 'bold',
                                        fontSize: '0.85rem',
                                    },
                                }}
                            />

                            <Container style={{ alignItems: 'center' }}>
                                <DamageStatusChangeButtons
                                    damages={damage}
                                    handleStatusChange={handleStatusChange}
                                    workspaceUser={workspaceUser}
                                />
                                <Tooltip title="View All Move In Issues">
                                    <IconButton
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            setShowCurrentTenantsModal(true)
                                            fetchCurrentTenantLeases()
                                        }}
                                        disabled={isLoadingLeases}
                                        size="small"
                                    >
                                        <GroupIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                                {onClickDamageButton && (
                                    <Tooltip title="Damages">
                                        <IconButton
                                            onClick={(e) => {
                                                e.stopPropagation()
                                                onClickDamageButton()
                                            }}
                                        >
                                            <Build fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                )}
                                <IconButton
                                    onClick={(e) => {
                                        e.stopPropagation() // Prevent row selection
                                        props.onOpenMessageDrawer(
                                            damage.channel,
                                            damage.id,
                                        )
                                    }}
                                    size="small"
                                >
                                    <Badge
                                        badgeContent={damage.message_count}
                                        color="secondary"
                                    >
                                        <MessageIcon fontSize="small" />
                                    </Badge>
                                </IconButton>
                            </Container>
                        </Container>
                    </div>
                </Container>
            </Container>

            {/* Middle Column: AR Transactions */}
            <Container
                style={{
                    borderLeft: `1px solid ${theme.palette.grey[600]}`,
                    borderRight: `1px solid ${theme.palette.grey[600]}`,
                    minWidth: 510,
                    maxWidth: 510,
                    flexDirection: 'column',
                    padding: theme.spacing(1),
                    paddingTop: 0, // Remove top padding to align with left column
                }}
                onClick={(e) => e.stopPropagation()} // Prevent row selection when working with transactions
            >
                <Container
                    style={{
                        alignItems: 'center',
                        marginBottom: 4,
                        marginTop: 0, // Align with the top of left column
                        height: 28, // Match height with date row
                        paddingTop: theme.spacing(1), // Match the top padding of the left column
                    }}
                >
                    <span
                        style={{
                            ...typographyStyle(theme.typography.subtitle2),
                            fontWeight: 700, // Keep it bold
                        }}
                    >
                        AR Transactions
                        <span
                            style={{
                                ...typographyStyle(theme.typography.caption),
                                backgroundColor: theme.palette.grey[300],
                                borderRadius: '50%',
                                padding: '1px 6px',
                                marginLeft: 6,
                                fontWeight: 700,
                            }}
                        >
                            {damage.damage_transactions.length}
                        </span>
                    </span>

                    <div style={{ flex: 1 }} />

                    <div>
                        <Tooltip title={'Add Transaction'}>
                            <IconButton
                                disabled={damageDisabled}
                                onClick={(e) => {
                                    e.stopPropagation() // Prevent row selection
                                    openPopover(e, damage.id)
                                }}
                                size="small"
                                style={{ padding: 4 }}
                            >
                                <AddBoxIcon
                                    color={
                                        damageDisabled ? 'disabled' : 'primary'
                                    }
                                    fontSize="small"
                                />
                            </IconButton>
                        </Tooltip>
                    </div>
                </Container>

                {/* Transactions list */}
                <Container style={{ flexWrap: 'wrap', flex: 1 }} scrollY>
                    {damage.damage_transactions.length === 0 ? (
                        <Container
                            style={{
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                height: '100%',
                                minHeight: '180px',
                                color: theme.palette.text.secondary,
                                width: '100%',
                                textAlign: 'center',
                            }}
                        >
                            <span
                                style={{
                                    ...typographyStyle(theme.typography.body2),
                                }}
                            >
                                No transactions found
                            </span>
                            <span
                                style={{
                                    ...typographyStyle(
                                        theme.typography.caption,
                                    ),
                                }}
                            >
                                Click the + button to add transactions
                            </span>
                        </Container>
                    ) : (
                        damage.damage_transactions.map((dt) => {
                            return (
                                <DamageTransactionRow
                                    key={`DAMAGE_TRANSACTION_${dt.id}`}
                                    damageTransaction={dt}
                                    damage={damage}
                                    totalCost={damage.amount * damage.cost}
                                    splitBy={damage.damage_transactions.length}
                                    removeDamageTransaction={() =>
                                        props.removeDamageTransaction(
                                            dt,
                                            damage,
                                        )
                                    }
                                    displayProblems={displayProblems}
                                    openMoveInModal={() => {
                                        if (invConfigMap) {
                                            console.log(dt)
                                            setLease(dt.lease)
                                        }
                                    }}
                                />
                            )
                        })
                    )}
                </Container>
            </Container>

            {/* Right Column: Attachments */}
            <Container
                style={{
                    padding: theme.spacing(1),
                    paddingBottom: 0,
                    paddingRight: 0,
                    alignItems: 'center',
                    overflowX: 'auto',
                }}
                onClick={(e) => e.stopPropagation()} // Prevent row selection when working with attachments
            >
                {damage.attachments.map((attachment, index) => {
                    const isSelected =
                        damage.attachment_to_sync === null
                            ? index === 0
                            : attachment.id === damage.attachment_to_sync
                    return (
                        <div
                            key={`ATTACHMENT_${attachment.id}`}
                            style={{
                                cursor: 'pointer',
                                border: isSelected
                                    ? `3px solid ${theme.palette.primary.dark}`
                                    : ``,
                                borderRadius: theme.shape.borderRadius,
                                padding: theme.spacing(1, 0, 0, 1),
                                margin: theme.spacing(0.5),
                                position: 'relative',
                                transition: 'all 0.2s ease-in-out',
                                boxShadow: isSelected
                                    ? `0 0 0 2px ${theme.palette.primary.light}`
                                    : 'none',
                                display: 'inline-block',
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'scale(1.02)'
                                e.currentTarget.style.boxShadow = `0 4px 8px ${theme.palette.grey[400]}`
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'scale(1)'
                                e.currentTarget.style.boxShadow = isSelected
                                    ? `0 0 0 2px ${theme.palette.primary.light}`
                                    : 'none'
                            }}
                            onClick={(e) => {
                                e.stopPropagation() // Prevent row selection
                                if (
                                    isSelected ||
                                    damage.status ===
                                        DamageStatus.SYNC_PENDING ||
                                    damage.status ===
                                        DamageStatus.ENTRATA_SYNCED
                                ) {
                                    window.open(attachment.file)
                                } else {
                                    const request: UpdateDamageRequest = {
                                        attachment_to_sync: isSelected
                                            ? undefined
                                            : attachment.id,
                                    }
                                    updateDamage(damage, request).catch((e) => {
                                        toast.error(
                                            `Error updating attachment sync ${e}`,
                                        )
                                    })
                                }
                            }}
                        >
                            <Attachment
                                attachment={attachment.file}
                                uploaded={attachment.uploaded}
                                theme={theme}
                                onClick={(e) => {
                                    e.stopPropagation()
                                    if (
                                        isSelected ||
                                        damage.status ===
                                            DamageStatus.SYNC_PENDING ||
                                        damage.status ===
                                            DamageStatus.ENTRATA_SYNCED
                                    ) {
                                        window.open(attachment.file)
                                    } else {
                                        const request: UpdateDamageRequest = {
                                            attachment_to_sync: isSelected
                                                ? undefined
                                                : attachment.id,
                                        }
                                        updateDamage(damage, request).catch(
                                            (e) => {
                                                toast.error(
                                                    `Error updating attachment sync ${e}`,
                                                )
                                            },
                                        )
                                    }
                                }}
                            />
                            {isSelected && (
                                <div
                                    style={{
                                        position: 'absolute',
                                        top: -8,
                                        right: -8,
                                        backgroundColor:
                                            theme.palette.primary.dark,
                                        color:
                                            theme.palette.primary.contrastText,
                                        borderRadius: '50%',
                                        width: 20,
                                        height: 20,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: 12,
                                        fontWeight: 'bold',
                                    }}
                                >
                                    ✓
                                </div>
                            )}
                        </div>
                    )
                })}
            </Container>

            <div style={{ flex: 1 }} />
            {invConfigMap && areaConfigMap && unitMap && (
                <>
                    <MoveInModal
                        open={lease !== undefined}
                        handleClose={() => setLease(undefined)}
                        areaConfigMap={areaConfigMap}
                        unitMap={unitMap}
                        invConfigMap={invConfigMap}
                        lease={lease}
                        serviceList={[]}
                        vendorList={[]}
                        userList={[]}
                        scheduleList={[]}
                        readOnly
                    />
                    <CurrentTenantsModal
                        open={showCurrentTenantsModal}
                        handleClose={() => setShowCurrentTenantsModal(false)}
                        areaConfigMap={areaConfigMap}
                        unitMap={unitMap}
                        invConfigMap={invConfigMap}
                        leases={currentTenantLeases}
                        isLoading={isLoadingLeases}
                    />
                </>
            )}
            {invConfigMap && areaConfigMap && unitMap && (
                <MoveInModal
                    open={issueLeaseOpen}
                    handleClose={() => setIssueLeaseOpen(false)}
                    areaConfigMap={areaConfigMap}
                    invConfigMap={invConfigMap}
                    unitMap={unitMap}
                    lease={issueFilteredLease}
                    serviceList={[]}
                    vendorList={[]}
                    userList={[]}
                    scheduleList={[]}
                    readOnly
                    hideIssueCount
                />
            )}
        </Container>
    )
}

const LEFT_COL_W = 300
const CHECK_BOX_W = 42
